import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CountryIso2, HistoryActionResult, ParsedCountry, PhoneFormattingConfig, SetStateConfig, UseHistoryStateConfig, UsePhoneFieldOptions, UsePhoneInputConfig } from '.';

import { defaultCountries, PhoneNumberManager } from '.';

export function useTimer() {
  const prevTimeRef = useRef<number | null>(null);
  const currentTimeRef = useRef<number>(Date.now());

  /**
   * @returns a milliseconds difference from the last check() call.
   * On first call returns undefined
   */
  const check = (): number | undefined => {
    const newTime: number = Date.now();
    const difference: number | undefined = prevTimeRef.current ? newTime - currentTimeRef.current : undefined;

    prevTimeRef.current = currentTimeRef.current;
    currentTimeRef.current = newTime;

    return difference;
  };

  return {
    check,
  };
}

export function useHistoryState<T extends Record<string, unknown> | string>(initialValue: T | (() => T), config?: UseHistoryStateConfig<T>) {
  const manager = useMemo(() => {
    return new PhoneNumberManager();
  }, []);
  const { size, overrideLastItemDebounceMS, onChange } = {
    ...manager.getDefaultHistoryConfig,
    ...config,
  };

  const [state, _setState] = useState<T>(initialValue);
  const [history, setHistory] = useState<T[]>([state]);
  const [pointer, setPointer] = useState<number>(0);

  const timer = useTimer();

  const setState = (value: T, config?: SetStateConfig) => {
    if (
      // compare entries if passed value is object
      (typeof value === 'object' && typeof state === 'object' && manager.isShallowEqualObjects(value, state)) ||
      value === state
    ) {
      return;
    }

    const isOverridingEnabled = overrideLastItemDebounceMS > 0;

    const timePassedSinceLastChange = timer.check();
    const debounceTimePassed = isOverridingEnabled && timePassedSinceLastChange !== undefined ? timePassedSinceLastChange > overrideLastItemDebounceMS : true;

    const shouldOverrideLastItem =
      // use value of config.overrideLastItem if passed
      config?.overrideLastItem !== undefined ? config.overrideLastItem : !debounceTimePassed;

    if (shouldOverrideLastItem) {
      // do not update pointer
      setHistory((prev) => {
        return [...prev.slice(0, pointer), value];
      });
    } else {
      const isSizeOverflow = history.length >= size;
      setHistory((prev) => [...prev.slice(isSizeOverflow ? 1 : 0, pointer + 1), value]);
      if (!isSizeOverflow) {
        setPointer((prev) => prev + 1);
      }
    }
    _setState(value);
    onChange?.(value);
  };

  const undo = (): HistoryActionResult<T> => {
    if (pointer <= 0) {
      return { success: false };
    }

    const value = history[pointer - 1];
    if (value === undefined) {
      return { success: false };
    }
    _setState(value);
    setPointer((prev) => prev - 1);

    onChange?.(value);
    return { success: true, value };
  };

  const redo = (): HistoryActionResult<T> => {
    if (pointer + 1 >= history.length) {
      return { success: false };
    }

    const value = history[pointer + 1];
    if (value === undefined) {
      return { success: false };
    }
    _setState(value);
    setPointer((prev) => prev + 1);

    onChange?.(value);
    return { success: true, value };
  };

  return [state, setState, undo, redo] as const;
}

export const usePhoneInput = (phoneInputProps: UsePhoneInputConfig) => {
  const manager = useMemo(() => {
    return new PhoneNumberManager();
  }, []);
  const defaultConfig = manager.getDefaultConfig;

  const {
    defaultCountry = defaultConfig.defaultCountry,
    value = defaultConfig.value,
    countries = defaultConfig.countries,
    prefix = defaultConfig.prefix,
    defaultMask = defaultConfig.defaultMask,
    charAfterDialCode = defaultConfig.charAfterDialCode,
    historySaveDebounceMS = defaultConfig.historySaveDebounceMS,
    disableCountryGuess = defaultConfig.disableCountryGuess,
    disableDialCodePrefill = defaultConfig.disableDialCodePrefill,
    forceDialCode: forceDialCodeConfig = defaultConfig.forceDialCode,
    disableDialCodeAndPrefix = defaultConfig.disableDialCodeAndPrefix,
    disableFormatting = defaultConfig.disableFormatting,
    onChange,
    inputRef: inputRefProp,
  } = phoneInputProps;

  const countryGuessingEnabled: boolean = !disableCountryGuess;
  const forceDialCode: boolean = disableDialCodeAndPrefix ? false : forceDialCodeConfig;

  const phoneFormattingConfig: PhoneFormattingConfig = {
    countries,
    prefix,
    charAfterDialCode,
    forceDialCode,
    disableDialCodeAndPrefix,
    defaultMask,
    countryGuessingEnabled,
    disableFormatting,
  };

  const ref = useRef<HTMLInputElement | null>(null);
  const inputRef = inputRefProp || ref;

  const setCursorPosition = (cursorPosition: number) => {
    /**
     * HACK: should set cursor on the next tick to make sure that the phone value is updated
     * useTimeout with 0ms provides issues when two keys are pressed same time
     */
    Promise.resolve().then(() => {
      // workaround for safari autofocus bug:
      // Check if the input is focused before setting the cursor, otherwise safari sometimes autofocuses on setSelectionRange
      if (typeof window === 'undefined' || inputRef.current !== document.activeElement) {
        return;
      }

      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  /**
   * phone - E.164 formatted phone
   * inputValue - value that should be rendered in the input element
   * country - current country code
   */
  const [{ phone, inputValue, country }, updateHistory, undo, redo] = useHistoryState(
    () => {
      const defaultCountryFull = manager.getCountry({
        value: defaultCountry,
        field: 'iso2',
        countries,
      });

      if (!defaultCountryFull) {
        // default country is not passed, or iso code do not match
        console.error(`[phone-number]: can not find a country with "${defaultCountry}" iso2 code`);
      }

      const initialCountry =
        defaultCountryFull || // fallback to "us" if user provided not valid country
        (manager.getCountry({
          value: 'us',
          field: 'iso2',
          countries,
        }) as ParsedCountry);

      const {
        phone,
        inputValue,
        country: formatCountry,
      } = manager.handlePhoneChange({
        value,
        country: initialCountry,
        insertDialCodeOnEmpty: !disableDialCodePrefill,
        ...phoneFormattingConfig,
      });

      setCursorPosition(inputValue.length);

      return {
        phone,
        inputValue,
        country: formatCountry.iso2,
      };
    },
    {
      overrideLastItemDebounceMS: historySaveDebounceMS,
      onChange: ({ inputValue, phone, country }) => {
        if (!onChange) return;

        const fullCountry = getFullCountry(country);
        onChange({
          phone,
          inputValue,
          country: fullCountry,
        });
      },
    }
  );

  const getFullCountry = useCallback(
    (iso2: string): ParsedCountry => {
      return manager.getCountry({
        value: iso2,
        field: 'iso2',
        countries,
      }) as ParsedCountry;
    },
    [countries]
  );

  const fullCountry = useMemo((): ParsedCountry => {
    return getFullCountry(country);
  }, [country, getFullCountry]);

  // Handle undo/redo events
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // Keydown event without key property throws on autofill
      if (!e.key) return;

      const ctrlPressed: boolean = e.ctrlKey;
      const metaPressed: boolean = e.metaKey;
      const shiftPressed: boolean = e.shiftKey;
      const zPressed: boolean = e.key.toLowerCase() === 'z';

      if (!zPressed) return;

      if (manager.isMacOS()) {
        // command+z on macOS
        if (!metaPressed) return;
      } else {
        // ctrl+z on non-macOS
        if (!ctrlPressed) return;
      }

      if (shiftPressed) {
        redo();
      } else {
        undo();
      }
    };

    input.addEventListener('keydown', onKeyDown);
    return () => {
      input.removeEventListener('keydown', onKeyDown);
    };
  }, [inputRef, undo, redo]);

  const handlePhoneValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): string => {
    e.preventDefault();

    const {
      phone: newPhone,
      inputValue: newInputValue,
      country: newCountry,
      cursorPosition: newCursorPosition,
    } = manager.handleUserInput(e, {
      country: fullCountry,
      phoneBeforeInput: inputValue,
      insertDialCodeOnEmpty: false, // allow user to clear input

      ...phoneFormattingConfig,
    });

    updateHistory({
      inputValue: newInputValue,
      phone: newPhone,
      country: newCountry.iso2,
    });

    setCursorPosition(newCursorPosition);

    return value;
  };

  const setCountry = (countryIso2: CountryIso2, options = { focusOnInput: false }) => {
    const newCountry = manager.getCountry({
      value: countryIso2,
      field: 'iso2',
      countries,
    });
    if (!newCountry) {
      console.error(`[phone-number]: can not find a country with "${countryIso2}" iso2 code`);
      return;
    }

    const inputValue: string = disableDialCodeAndPrefix ? '' : `${prefix}${newCountry.dialCode}${charAfterDialCode}`;

    updateHistory({
      inputValue,
      phone: `${prefix}${newCountry.dialCode}`,
      country: newCountry.iso2,
    });

    if (options.focusOnInput) {
      // Next tick is used to support UI libraries (had an issue with MUI)
      Promise.resolve().then(() => {
        inputRef.current?.focus();
      });
    }
  };

  const [initialized, setInitialized] = useState(false);

  // Handle value update
  useEffect(() => {
    if (!initialized) {
      setInitialized(true);

      if (value !== phone) {
        // Can call onChange directly because phone value was formatted inside the useHistoryState setter
        onChange?.({
          inputValue,
          phone,
          country: fullCountry,
        });
      }

      // skip value handling on initial render
      return;
    }

    if (value === phone) return;

    // new value has been provided to the "value" prop (updated not via input field)

    const {
      phone: newPhone,
      inputValue: newInputValue,
      country: newCountry,
    } = manager.handlePhoneChange({
      value,
      country: fullCountry,
      insertDialCodeOnEmpty: !disableDialCodePrefill,
      ...phoneFormattingConfig,
    });

    updateHistory({
      phone: newPhone,
      inputValue: newInputValue,
      country: newCountry.iso2,
    });
  }, [value]);

  return {
    phone, // Phone in E164 format
    inputValue, // Formatted phone string. Value that should be rendered inside input element.
    country: fullCountry, // Current country object.
    setCountry, // Country setter.
    handlePhoneValueChange, // Change handler for input component
    inputRef, // Ref object for input component (handles caret position, focus and undo/redo).
  };
};

export const usePhoneField = (phoneFieldOptions: UsePhoneFieldOptions) => {
  const { flags: _flags, disableFocusAfterCountrySelect = false, countries = defaultCountries, ...phoneInputConfig } = phoneFieldOptions;

  // Core phone formatting/state from the repo hook
  const { phone, inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    ...phoneInputConfig,
    countries,
  });

  const manager = useMemo(() => {
    return new PhoneNumberManager();
  }, []);

  // Search state for filtering countries
  const [search, setSearch] = useState<string>('');

  const parsedCountries = useMemo<ParsedCountry[]>(() => countries.map(manager.parseCountry), [countries, manager]);

  // Put preferred countries (if any) on top (preserve overall order)
  const orderedCountries = useMemo<ParsedCountry[]>(() => {
    const preferred = new Set((phoneInputConfig.preferredCountries || []).map((c) => c.toLowerCase()));
    if (!preferred.size) return parsedCountries;

    const pref = parsedCountries.filter((c) => preferred.has(c.iso2));
    const rest = parsedCountries.filter((c) => !preferred.has(c.iso2));
    return [...pref, ...rest];
  }, [parsedCountries, phoneInputConfig.preferredCountries]);

  // Filtered list by search
  const filteredCountries = useMemo<ParsedCountry[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderedCountries;

    // match by name, iso2, or dialCode (with or without +)
    const cleaned: string = q.replace(/^\+/, '');
    return orderedCountries.filter((c) => {
      return c.name.toLowerCase().includes(q) || c.iso2.toLowerCase().includes(q) || (c.dialCode && c.dialCode.toString().includes(cleaned));
    });
  }, [orderedCountries, search]);

  // Handle selecting a country from the dropdown
  const selectCountry = useCallback(
    (iso2: CountryIso2) => {
      setCountry(iso2, { focusOnInput: !disableFocusAfterCountrySelect });
    },
    [setCountry, disableFocusAfterCountrySelect]
  );

  // Ready-to-spread input props for your custom input component
  const inputProps = {
    value: inputValue,
    onChange: handlePhoneValueChange,
    type: 'tel' as const,
    ref: inputRef,
  };

  return {
    // phone state
    phone, // E.164 format
    inputValue, // formatted display value
    country, // current ParsedCountry
    setCountry, // (iso2, { focusOnInput?: boolean })
    handlePhoneValueChange,
    inputRef,

    // input convenience
    inputProps,

    // search + country list for dropdown
    search,
    setSearch,
    countries: filteredCountries,
    selectCountry,
  };
};
